import React, { type FC, type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack } from '@mui/material';

import { useGetProcessStats } from '@/api/generated';
import { HorizontalTab, HorizontalTabs } from '@/components/Tabs';
import CountCard from '@/containers/Talents/CountCard';
import { delay } from '@/utils/delay.ts';

import TalentsTab from './TalentsTab';
import TestList from './TestList';
import TestsTab from './TestsTab';

interface ProcessSidebarProps {
  processId: string;
}

const CHART_SIZE = 136;

const ProcessSidebar: FC<ProcessSidebarProps> = ({ processId }) => {
  const { t } = useTranslation('processes');
  const [value, setValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const { data: processStats } = useGetProcessStats(processId);
  const { tests, testStats, talents: talentsStats } = processStats || {};
  const { inProgress, completed, total } = talentsStats || {};

  useEffect(() => {
    delay(1000).then(() => setIsLoading(false));
  }, []);

  const handleChange = useCallback((_event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  }, []);

  return (
    <Box sx={{ padding: 3, maxWidth: 600, margin: '0 auto' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
        gap={1}
        mb={3}
        sx={{ '& .CountCard-root': { bgcolor: 'grey.100', boxShadow: 'none' } }}
      >
        <CountCard
          isLoading={isLoading}
          label={t('layouts.processes.details.info.counters.total')}
          count={total || 0}
        />
        <CountCard
          isLoading={isLoading}
          label={t('layouts.processes.details.info.counters.started')}
          count={inProgress || 0}
        />
        <CountCard
          isLoading={isLoading}
          label={t('layouts.processes.details.info.counters.finished')}
          count={completed || 0}
        />
      </Stack>

      <HorizontalTabs value={value} onChange={handleChange}>
        <HorizontalTab label={t('layouts.processes.details.info.tabs.talents')} />
        <HorizontalTab label={t('layouts.processes.details.info.tabs.tests')} />
      </HorizontalTabs>
      <Box>
        {value === 0 && <TalentsTab counters={talentsStats || {}} chartSize={CHART_SIZE} />}
        {value === 1 && <TestsTab tests={tests} testStats={testStats!} chartSize={CHART_SIZE} />}
      </Box>
      <Divider sx={{ my: 3 }} />
      <TestList tests={tests} processId={processId} />
    </Box>
  );
};

export default ProcessSidebar;
